import { useState } from "react";
import { usePlayerDeletion } from "@/hooks/usePlayerDeletion";
import { Player } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Undo2 } from "lucide-react";
import AvatarPlaceholder from "@/components/ui/avatar-placeholder";
import { useUnitsPreference } from "@/hooks/useUnitsPreference";
import { convertHeight, convertWeight } from "@/lib/unitConversion";
import { format } from "date-fns";

export default function DeletedPlayersTable() {
  const { deletedPlayers, isLoadingDeleted, restorePlayer, isRestoring } = usePlayerDeletion();
  const { isMetric } = useUnitsPreference();
  const [restoringPlayerId, setRestoringPlayerId] = useState<number | null>(null);

  const handleRestore = (playerId: number) => {
    setRestoringPlayerId(playerId);
    restorePlayer(playerId, {
      onSettled: () => setRestoringPlayerId(null),
    });
  };

  const getDeletionReasonLabel = (reason: string) => {
    switch (reason) {
      case 'not_interested':
        return "Not interested";
      case 'duplicate':
        return "Duplicate";
      case 'not_a_player':
        return "Not a player";
      default:
        return reason;
    }
  };

  const getPlayingLevelBadge = (level: string) => {
    const badgeClass = 
      level === 'D1' ? 'bg-green-100 text-green-800' :
      level === 'D2' ? 'bg-blue-100 text-blue-800' :
      level === 'Professional Academy' ? 'bg-purple-100 text-purple-800' :
      'bg-neutral-100 text-neutral-800';
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>
        {level}
      </span>
    );
  };

  if (isLoadingDeleted) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (deletedPlayers.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-12">
        <div className="text-center">
          <Undo2 className="mx-auto h-12 w-12 text-neutral-400 mb-4" />
          <h3 className="text-lg font-medium text-neutral-900 mb-2">No deleted players</h3>
          <p className="text-neutral-500">Players you remove will appear here, where you can restore them if needed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden w-full">
      <div className="px-6 py-4 border-b border-neutral-200">
        <h2 className="text-lg font-medium text-neutral-900">
          Deleted Players ({deletedPlayers.length})
        </h2>
        <p className="text-sm text-neutral-500 mt-1">
          Players you've removed from your database. You can restore them at any time.
        </p>
      </div>
      
      <div className="w-full">
        <table className="w-full divide-y divide-neutral-200">
          <thead className="bg-neutral-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-1/5">
                Player
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-1/8">
                Positions & Details
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-1/8">
                Height/Weight
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-1/8">
                Current Club
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-1/8">
                Playing Level
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-1/10">
                High School Grad Year
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-1/8">
                Deletion Info
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider w-1/10">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-neutral-200">
            {deletedPlayers.map((player) => (
              <tr key={player.id} className="hover:bg-neutral-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <AvatarPlaceholder name={player.name} className="h-10 w-10 flex-shrink-0"/>
                    <div className="ml-3 min-w-0 flex-1">
                      <div className="text-sm font-medium text-neutral-900 truncate">
                        {player.name}
                      </div>
                      {player.age && (
                        <div className="text-sm text-neutral-500">
                          Age: {player.age}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                  <div className="space-y-1">
                    <div className="truncate">
                      <span className="font-medium">General:</span> {player.position?.join(', ') || '-'}
                    </div>
                    {player.specific_positions && player.specific_positions.length > 0 && (
                      <div className="truncate">
                        <span className="font-medium">Specific:</span> {player.specific_positions.join(', ')}
                      </div>
                    )}
                    {player.age && (
                      <div className="text-neutral-500">
                        <span className="font-medium">Age:</span> {player.age} years
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                  <div className="space-y-1">
                    <div>
                      {player.height ? convertHeight(player.height, isMetric) : '-'}
                    </div>
                    <div className="text-neutral-500">
                      {player.weight ? convertWeight(player.weight, isMetric) : '-'}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                  <div className="truncate">
                    {player.current_club || '-'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                  {player.playing_level ? getPlayingLevelBadge(player.playing_level) : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                  {player.hs_grad_year || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="space-y-1">
                    <div className="text-sm text-neutral-900">
                      {getDeletionReasonLabel(player.deletion_info?.reason || '')}
                    </div>
                    <div className="text-sm text-neutral-500">
                      {player.deletion_info?.deleted_at
                        ? format(new Date(player.deletion_info.deleted_at), 'MMM d, yyyy')
                        : '-'}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRestore(player.id)}
                    disabled={isRestoring && restoringPlayerId === player.id}
                    className="transition-all hover:bg-primary hover:text-white"
                  >
                    {isRestoring && restoringPlayerId === player.id ? (
                      <>
                        <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary mr-2"></span>
                        Restoring...
                      </>
                    ) : (
                      <>
                        <Undo2 className="h-4 w-4 mr-2" />
                        Restore
                      </>
                    )}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
} 